import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import { MdPhone as PhoneIcon, MdEmail as EmailIcon, MdPerson as PersonIcon } from "react-icons/md";
import toast from "react-hot-toast";
import { saveDentistaContato } from "requests/DentistaContato";

interface ContactUpdateModalProps {
  open: boolean;
  userId: string;
  dentistName: string;
  onSaved: () => void;
}

const overlayStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: 420 },
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 0,
  overflow: "hidden",
};

const ContactUpdateModal: React.FC<ContactUpdateModalProps> = ({
  open,
  userId,
  dentistName,
  onSaved,
}) => {
  const [telefone, setTelefone] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [telefoneError, setTelefoneError] = useState("");

  const formatPhone = (value: string) => {
    const digits = value.replace(/\D/g, "").slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7)
      return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    if (digits.length <= 10)
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    setTelefone(formatted);
    if (telefoneError) setTelefoneError("");
  };

  const handleSubmit = async () => {
    const digits = telefone.replace(/\D/g, "");
    if (digits.length < 10) {
      setTelefoneError("Informe um telefone válido com DDD.");
      return;
    }

    setLoading(true);
    try {
      const payload: { telefone: string; email?: string } = { telefone };
      if (email.trim()) payload.email = email.trim();

      const { status, data } = await saveDentistaContato(userId, payload);

      if ([200, 201].includes(status) && data.success) {
        toast.success(data.message);
        onSaved();
      } else {
        toast.error(data.message || "Erro ao salvar contato.");
      }
    } catch {
      toast.error("Erro ao salvar contato. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={() => { }} disableEscapeKeyDown>
      <Box sx={overlayStyle}>
        <Box
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
            px: 3,
            py: 3,
            color: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <PersonIcon fontSize="small" />
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Olá, {dentistName}
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight={600}>
            Atualize o seu cadastro
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
            Preencha os campos abaixo para continuar utilizando o sistema.
          </Typography>
        </Box>

        <Box sx={{ px: 3, py: 3, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <TextField
            label="Telefone"
            placeholder="(00) 00000-0000"
            value={telefone}
            onChange={handlePhoneChange}
            error={!!telefoneError}
            helperText={telefoneError || "Obrigatório"}
            fullWidth
            required
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon color={telefoneError ? "error" : "action"} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="E-mail"
            placeholder="seu@email.com"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            helperText="Opcional"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleSubmit}
            disabled={loading || !telefone.trim()}
            sx={{
              mt: 1,
              py: 1.5,
              borderRadius: 2,
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Salvar e continuar"
            )}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default ContactUpdateModal;
